import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import Avatar from 'react-avatar';
import Griddle, { plugins, RowDefinition, ColumnDefinition } from 'griddle-react';
import {
  ButtonGroup,
  Button,
} from 'react-bootstrap';

import { displayBuyerBidState } from '../../../core/api';
import { findById } from '../../../core/utils';
import { fullDate } from '../../../core/date';
import BidDetailModal from './BidDetailModal';
import BidUpdateModal from './BidUpdateModal';

const styleConfig = {
  icons: {
    TableHeadingCell: {
      sortDescendingIcon: '▼',
      sortAscendingIcon: '▲',
    },
  },
  classNames: {
    Filter: 'bid-filter',
  },
  styles: {
    Filter: { fontSize: 18 },
  },
};

const BidsRowComponent = connect((state, props) => ({
  bid: plugins.LocalPlugin.selectors.rowDataSelector(state, props)
}))(({ bid }) => (
  <div className="col-sm-12">
    <div className="row manage-bids-row">
      <div className="col-sm-3 bids-image-container">
        {bid.image && (
          <img src={bid.image} />
        )}
        {!bid.image && (
          <Avatar 
            name={ bid.name.split(' ').length > 1 ? bid.name.split(' ').slice(0, 2).join(' ') : bid.name } 
            color="green" 
            maxInitials={2} 
          />
        )}
      </div>
      <div className="col-sm-9">
        <div className="row bids-row-content">
          <div className="col-sm-6">
            <h2>{bid.name}</h2>
            <p>{bid.distance} mi.</p>
            <p>{bid.location}</p>
            <p className="bid-pullBottom">{bid.state}</p>
          </div>
          <div className="col-sm-6">
            <h3>{bid.group}</h3>
            <p>{bid.start} - {bid.end}</p>
            <p>Suite: {bid.suite}</p>
            <p>King: {bid.king}</p>
            <p>Double: {bid.queen}</p>
            <ButtonGroup className="show-on-hover bid-pullBottom">
              <Button bsStyle="default" onClick={(bid.showBidDetails)}>
                <i className="fa fa-pencil fa-lg" /> Manage Bid
              </Button>
              {bid.showToggle && (
                <Button 
                  bsStyle={bid.share ? 'success' : 'danger'}
                  onClick={bid.toggleShareBid}
                >
                  {bid.share && (
                    <i className="fa fa-eye" />
                  )}
                  {!bid.share && (
                    <i className="fa fa-eye-slash" />
                  )}
                </Button>
              )}
            </ButtonGroup>
          </div>
        </div>
      </div>
    </div>
  </div>
));

const BidsTableComponent = OriginalComponent => class BidsTableComponent extends React.Component {
  static contextTypes = {
    components: PropTypes.object
  }

  render() {
    return <this.context.components.TableBody />
  }
}

const BidsTableBody = ({ rowIds, Row, style, className }) => (
  <div className="row">
    { rowIds && rowIds.map(r => <Row key={r} griddleKey={r} />) }
  </div>
);

const sortProperties = [
  { id: 'start', sortAscending: true },
  { id: 'group', sortAscending: true },
  { id: 'name', sortAscending: true },
];
// DR: 'Draft',
// UK: 'Unsent',
// SG: 'Sending',
// ST: 'Sent',
// UR: 'Unread',
// VW: 'Viewed',
// RE: 'Replied',
// PD: 'Pending',
// SO: 'Soldout',
// IN: 'Interested',
// DE: 'Decline',
// DD: 'Declined',
// EX: 'Expired',
// AC: 'Accept',
// AD: 'Accepted',
// CA: 'Cancel',
// CD: 'Cancelled',
// CP: 'Contract Pending',
// CO: 'Contracted',

const tourManagerFilter = { DR: true, ST: true, NB: true, DE: true };

class BidsetsTable extends React.Component {
  constructor(props) {
    super(props);
    if (props.detail) {
      this.state = {
        showBid: true,
        bid: props.detail,
      };
    } else {
      this.state = {
        showBid: false,
      };
    }
    this.showBid = this.showBid.bind(this);
    this.closeBid = this.closeBid.bind(this);
  }
  closeBid() {
    this.setState({
      showBid: false,
    });
  }
  showBid(id) {
    this.setState({
      bid: id,
      showBid: true,
    });
  }
  findBid(id) {
    const idx = this.props.bids.findIndex(findById, id);
    if (idx === -1) {
      console.log('unable to find bid...', id);
      return {};
    }
    return this.props.bids[idx];
  }
  toggleShare(id, prevShareState) {
    this.props.editBid(id, {
      share: !prevShareState
    })
  }

  render() {
    const ButtonColumn = ({ value }) => (
      <ButtonGroup className="manage-bids-toolbar">
        <Button bsStyle="default" data-item={value} onClick={this.showBid}>
          <i className="fa fa-pencil fa-lg" /> Manage Bid
        </Button>
        {!this.props.isTourManager && (
          <HideButton bid={this.findBid(value)} editBid={this.props.editBid}/>
        )}
      </ButtonGroup>
    );

    const bids = [];
    this.props.bids.forEach((bid) => {
      // Filter bids with filter or hardcoded filter for tour manager
      if (this.props.filter[bid.buyer_state]) {
        return;
      }
      if (this.props.filter[bid.bidset.event.group_name]) {
        return;
      }
      // Don't show hidden bids to Tour Manager
      if (this.props.isTourManager && !bid.share) {
        return;
      }

      const displayState = displayBuyerBidState(bid.buyer_state, bid.bidder_state);
      const newbid = {
        id: bid.id,
        name: bid.hotel.name,
        image: bid.hotel.image,
        group: bid.bidset.event.group_name,
        location: `${bid.hotel.locality}, ${bid.hotel.region}`,
        distance: parseFloat(bid.distance_to_venue * 0.621371).toFixed(2),
        start: fullDate(bid.bidset.event.start),
        end: fullDate(bid.bidset.event.end),
        state: displayState,
        suite: bid.suite_rate ? bid.suite_rate : 'Unavailable',
        king: bid.king_rate ? bid.king_rate : 'Unavailable',
        queen: bid.queen_rate ? bid.queen_rate : 'Unavailable',
        share: bid.share,
        showToggle: !this.props.isTourManager,
        showBidDetails: (e) => {
          e.preventDefault();
          this.showBid(bid.id);
        },
        toggleShareBid: (e) => {
          e.preventDefault();
          this.toggleShare(bid.id, bid.share);
        }
      };
      bids.push(newbid);
    });

    return (
      <div>
        <Griddle
          data={bids}
          pageProperties={{
            currentPage: 1,
            pageSize: 200
          }}
          styleConfig={styleConfig}
          plugins={[plugins.LocalPlugin]}
          components={{
            Row: BidsRowComponent,
            TableContainer: BidsTableComponent,
            TableBody: BidsTableBody,
            SettingsToggle: (props) => null
          }}
        />
        { this.state.showBid && (
          <BidDetailModal
            bid={this.findBid(this.state.bid)}
            show={this.state.showBid}
            onClose={this.closeBid}
            addBidNote={this.props.addBidNote}
            editBid={this.props.editBid}
            sendBid={this.props.sendBid}
            removeBid={this.props.removeBid}
            isTourManager={this.props.isTourManager}
            readOnly={this.props.readOnly}
            user={this.props.user}
          />
        )}
        { this.props.showUpdate && (
          <BidUpdateModal
            bids={bids}
            show={this.props.showUpdate}
            onClose={this.props.closeUpdate}
            sendBidUpdate={this.props.sendBidUpdate}
            user={this.props.user}
          />
        )}
      </div>
    );
  }
}


BidsetsTable.propTypes = {
  bids: PropTypes.arrayOf(PropTypes.object).isRequired,
  isTourManager: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  detail: PropTypes.string,
  editBid: PropTypes.func.isRequired,
  addBidNote: PropTypes.func.isRequired,
  sendBid: PropTypes.func.isRequired,
  removeBid: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  showUpdate: PropTypes.bool.isRequired,
  closeUpdate: PropTypes.func.isRequired,
  sendBidUpdate: PropTypes.func.isRequired,
};

BidsetsTable.defaultProps = {
  bids: [],
  detail: null,
};
export default BidsetsTable;



// WEBPACK FOOTER //
// src/routes/tours/components/BidsetsTableDetached.js